import React, { useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import { useToggleState } from 'shared/hooks'
import {
  getGroupForId,
  GroupWithProducts,
  useCheckTreeAttributions,
} from './utility'
import { AttributionEdit } from '../components/tree/LeafView'
import { useGetTreeById } from '../reducers/trees_hooks'
import { allProductGroupsAtom } from '../api/masters.atoms'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { objectFieldSort } from '../utility'

export function AttributionValidation() {
  const groups = useAtomValue(allProductGroupsAtom).sort(
    objectFieldSort('name'),
  )
  return (
    <Box>
      <Box>
        <strong>Attribution Validation</strong>
      </Box>
      <Box padding="10px">
        {groups.map((group) => {
          return <ProductGroup key={group.id} groupId={group.id} />
        })}
      </Box>
    </Box>
  )
}

function ProductGroup({ groupId }: { groupId: number }) {
  const [expanded, toggleExpanded] = useToggleState(false)
  const [checkMasters, toggleCheckMasters] = useToggleState(false)
  const [checkEditables, toggleCheckEditables] = useToggleState(false)
  const [group, setGroup] = useState<GroupWithProducts>()

  useEffect(() => {
    getGroupForId(groupId).then(setGroup)
  }, [groupId])

  if (!group) return <Box>Loading...</Box>

  const hasData = checkMasters || checkEditables

  return (
    <Box padding={'10px'} border={hasData ? '1px solid' : ''}>
      <Box>
        <Button onClick={() => toggleExpanded()}>{group.name}</Button>
      </Box>
      {expanded && (
        <Box padding="10px">
          <Button disabled={checkMasters} onClick={() => toggleCheckMasters()}>
            Check Products
          </Button>
          <Button
            disabled={checkEditables}
            onClick={() => toggleCheckEditables()}
          >
            Check Editable Copies
          </Button>
        </Box>
      )}
      {hasData && (
        <Box padding="20px">
          {checkMasters && <CheckProducts group={group} />}
          {checkEditables && <CheckGroupEditableCopies group={group} />}
        </Box>
      )}
    </Box>
  )
}

function CheckProducts({ group }: { group: GroupWithProducts }) {
  const masterIds = group.products.reduce<
    { treeId: string; versionId: string }[]
  >((acc, p) => {
    if (!p.primary_tree) return acc
    return [
      ...acc,
      { treeId: p.primary_tree.tree_id, versionId: p.primary_tree.version_id },
    ]
  }, [])
  return (
    <Box>
      <Box>
        <strong>Products</strong>
      </Box>
      <Box padding="10px">
        {masterIds.map(({ treeId, versionId }) => (
          <TreeAttributionDisplay
            key={treeId}
            treeId={treeId}
            versionId={versionId}
          />
        ))}
      </Box>
    </Box>
  )
}

function CheckGroupEditableCopies({ group }: { group: GroupWithProducts }) {
  const ids = group.products.map(({ editable_tree }) => {
    return {
      treeId: editable_tree.tree_id,
      versionId: editable_tree.version_id,
    }
  })
  return (
    <Box>
      <Box>
        <strong>Editables</strong>
      </Box>
      <Box padding="10px">
        {ids.map(({ treeId, versionId }) => (
          <TreeAttributionDisplay
            key={treeId}
            treeId={treeId}
            versionId={versionId}
          />
        ))}
      </Box>
    </Box>
  )
}

function TreeAttributionDisplay(ids: { treeId: string; versionId: string }) {
  const { tree } = useGetTreeById(ids)
  return <CheckTreeAttribution {...ids} title={tree?.title ?? 'Attribution'} />
}

export function CheckTreeAttribution({
  title,
  ...ids
}: {
  treeId: string
  versionId: string
  title: string
}) {
  const [editing, toggleEditing] = useToggleState(false)
  const validationData = useCheckTreeAttributions(ids)
  if (!validationData) return <Box>Loading...</Box>
  const { attributions, valid, tree } = validationData
  return (
    <Box>
      {title}{' '}
      <span style={{ color: valid ? 'green' : 'red' }}>
        {attributions.length ? attributions.join(', ') : 'MISSING'}
      </span>
      <Button onClick={() => toggleEditing()}>Edit</Button>
      {editing && <AttributionEdit parentNode={tree} />}
    </Box>
  )
}
